import { useEffect, useState } from 'react'
import { Box, Modal, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CampaignIcon from '@mui/icons-material/Campaign'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { styled } from '@mui/material/styles'
import {
  AnnouncementItem,
  ANNOUNCEMENT_TYPES,
  fetchActiveAnnouncements,
  fetchReadAnnouncementIds,
  isDismissed,
  dismissAnnouncement,
  shouldShowAnnouncementsOnLogin,
  clearShowAnnouncementsOnLogin,
} from 'features/setting/announcement/announcement.utils'

import '../../styles/AnnouncementContent.css'
import cliniterLogo from 'assets/logo/cliniter-logo-white.png'
import { selectMe } from 'app/slice/user.slice'
import { useSelector } from 'react-redux'
import { getToken, getBranch, getServicePoint } from 'utils/app.utils'

const Backdrop = styled('div')({
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: -1,
})

const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95vw',
  maxWidth: 820,
  maxHeight: '92vh',
  backgroundColor: '#FFFFFF',
  borderRadius: 16,
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
})

const HeaderBanner = styled(Box)({
  background: 'linear-gradient(135deg, #1A3A5C 0%, #2F4269 50%, #1CB99A 100%)',
  padding: '24px 32px 20px',
  position: 'relative',
  overflow: 'hidden',
  flexShrink: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -60,
    right: -60,
    width: 200,
    height: 200,
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.05)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -40,
    left: -40,
    width: 150,
    height: 150,
    borderRadius: '50%',
    background: 'rgba(28, 185, 154, 0.15)',
  },
})

const ScrollBody = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '24px 32px',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: '#F0F0F0', borderRadius: 3 },
  '&::-webkit-scrollbar-thumb': { background: '#C0C0C0', borderRadius: 3, '&:hover': { background: '#A0A0A0' } },
})

const AcknowledgeButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  maxWidth: 280,
  margin: '0 auto',
  padding: '14px 32px',
  background: 'linear-gradient(135deg, #1CB99A, #17A589)',
  color: '#FFF',
  border: 'none',
  borderRadius: 10,
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 12px rgba(28, 185, 154, 0.3)',
  '&:hover': { background: 'linear-gradient(135deg, #17A589, #148F77)', boxShadow: '0 6px 16px rgba(28, 185, 154, 0.4)', transform: 'translateY(-1px)' },
  '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 8px rgba(28, 185, 154, 0.3)' },
})

const FooterArea = styled(Box)({
  padding: '16px 32px 20px',
  borderTop: '1px solid #E8EDF2',
  background: '#FAFBFC',
  flexShrink: 0,
})

/** ถอดรหัส HTML entities ในสตริง (เช่น &lt; &gt; &amp;) ให้เป็นแท็ก HTML จริง เพื่อให้เนื้อหาที่มี HTML ซ้อนหรือถูก escape แสดงผลได้ */
function decodeHtmlEntities(html: string): string {
  if (typeof document === 'undefined') return html
  const div = document.createElement('div')
  div.innerHTML = html
  return div.innerHTML
}

// =====================================================
// Dynamic Announcement Content (from management)
// =====================================================
function DynamicAnnouncementContent({ item }: { item: AnnouncementItem }) {
  if (!item?.content?.trim()) return null
  const decodedContent = decodeHtmlEntities(item.content)
  return (
    <Box
      className="announcement-html-content"
      dangerouslySetInnerHTML={{ __html: decodedContent }}
    />
  )
}

// =====================================================
// Single Announcement Popup
// =====================================================
function SingleAnnouncementModal({ item, onClose }: { item: AnnouncementItem; onClose: () => void }) {
  const title = item?.title || ''
  const typeLabel = item ? ANNOUNCEMENT_TYPES[item.type] : ''

  return (
    <Modal open onClose={onClose} slots={{ backdrop: Backdrop }} sx={{ zIndex: 9999 }}>
      <ModalContainer>
        <HeaderBanner>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)', zIndex: 1, '&:hover': { color: '#FFF', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {item?.type === 'PROVIDER' ? (
                <CampaignIcon sx={{ color: '#FFD54F', fontSize: 26 }} />
              ) : (
                <NotificationsActiveIcon sx={{ color: '#FFD54F', fontSize: 26 }} />
              )}
              <Box sx={{ color: '#FFD54F', fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
                {typeLabel}
              </Box>
            </Box>
            <Box
              component="img"
              src={cliniterLogo}
              alt="Cliniter"
              sx={{ height: { xs: 32, sm: 38 }, objectFit: 'contain', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
            />
          </Box>
          <Box sx={{ color: '#FFF', fontSize: { xs: 15, sm: 17 }, fontWeight: 700, lineHeight: 1.4, position: 'relative', zIndex: 1 }}>
            <div dangerouslySetInnerHTML={{ __html: title }} />
          </Box>
        </HeaderBanner>

        <ScrollBody>
          <DynamicAnnouncementContent item={item} />
        </ScrollBody>

        <FooterArea>
          <AcknowledgeButton onClick={onClose}>
            <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
            รับทราบ
          </AcknowledgeButton>
        </FooterArea>
      </ModalContainer>
    </Modal>
  )
}

// =====================================================
// Main AnnouncementPopup
// =====================================================
export default function AnnouncementPopup() {
  const [dynamicAnnouncements, setDynamicAnnouncements] = useState<AnnouncementItem[]>([])
  const [currentDynamicIndex, setCurrentDynamicIndex] = useState(0)
  const user = useSelector(selectMe)

  useEffect(() => {
    const token = getToken()
    const branchId = getBranch()
    const servicePointId = getServicePoint()
    if (!token || !branchId || !servicePointId) return

    Promise.all([fetchActiveAnnouncements(), fetchReadAnnouncementIds(user?.userId)]).then(([all, readIdsArray]) => {
      const showOnLogin = shouldShowAnnouncementsOnLogin()
      const readIds = new Set(readIdsArray)
      const undismissed = showOnLogin
        ? all.filter((a) => a.showAsPopup)
        : all.filter((a) => a.showAsPopup && !isDismissed(a.announcementId, readIds))
      setDynamicAnnouncements(undismissed)
    })
  }, [user?.userId])

  const handleCloseDynamic = () => {
    const current = dynamicAnnouncements[currentDynamicIndex]
    if (current) {
      dismissAnnouncement(current.announcementId, user?.userId || 0, user?.fullname || '')
    }
    if (currentDynamicIndex < dynamicAnnouncements.length - 1) {
      setCurrentDynamicIndex(currentDynamicIndex + 1)
    } else {
      clearShowAnnouncementsOnLogin()
      setDynamicAnnouncements([])
    }
  }

  if (dynamicAnnouncements.length > 0 && currentDynamicIndex < dynamicAnnouncements.length) {
    return (
      <SingleAnnouncementModal
        item={dynamicAnnouncements[currentDynamicIndex]}
        onClose={handleCloseDynamic}
      />
    )
  }

  return null
}
